import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Psychology as PsychologyIcon,
  FilePresent as FileIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';
import ReportGeneration from '../ChatBot/ReportGeneration';

const REPORT_TYPE_CONFIG = {
  '30_day_assessment': {
    name: '30-Day Assessment',
    icon: AssessmentIcon,
    color: '#2196F3',
    description: 'Organizational assessment report'
  },
  'leadership_development': {
    name: 'Leadership Development',
    icon: PsychologyIcon,
    color: '#9C27B0',
    description: 'Leadership development plan'
  },
  'team_dynamics': {
    name: 'Team Dynamics',
    icon: PeopleIcon,
    color: '#FF9800',
    description: 'Team effectiveness analysis'
  },
  'strategic_growth': {
    name: 'Strategic Growth',
    icon: TrendingUpIcon,
    color: '#4CAF50',
    description: 'Strategic growth analysis'
  }
};

const STATUS_COLORS = {
  draft: 'warning',
  final: 'success',
  generating: 'info'
};

// eslint-disable-next-line max-lines-per-function
const ReportDashboard = ({ entityId }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showReportGeneration, setShowReportGeneration] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [reportContent, setReportContent] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadReports();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const loadReports = async () => {
    if (!entityId) return;

    setLoading(true);
    setError(null);

    try {
      // Get reports from the reports collection
      const response = await api.getEntityReports(entityId);

      if (response.success && response.documents) {
        setReports(response.documents);
      } else {
        setReports([]);
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      setError('Failed to load reports');
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshReports = async () => {
    setRefreshing(true);
    await loadReports();
    setRefreshing(false);
  };

  const handleMenuOpen = (event, report) => {
    event.stopPropagation();
    setSelectedReport(report);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedReport(null);
  };

  const handleViewReport = async () => {
    if (!selectedReport) return;

    handleMenuClose();

    try {
      // For now, just display the content from the report
      setReportContent(selectedReport.content || 'No content available');
      setViewDialog(true);
    } catch (error) {
      console.error('Error viewing report:', error);
      setError('Failed to load report content');
    }
  };

  const handleDownloadReport = async () => {
    if (!selectedReport) return;

    handleMenuClose();

    try {
      const response = await api.exportToPDF({
        entityId: entityId,
        content: selectedReport.content,
        fileName: `${selectedReport.title || 'report'}.pdf`
      });

      if (response.success && response.downloadUrl) {
        // Open the download URL
        window.open(response.downloadUrl, '_blank');
      } else {
        throw new Error('PDF export failed');
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      setError('Failed to download report');
    }
  };

  const handleDeleteReport = async () => {
    if (!selectedReport) return;

    try {
      await api.deleteDocument('reports', selectedReport.id);
      await loadReports();
      setDeleteDialog(false);
      setSelectedReport(null);
    } catch (error) {
      console.error('Error deleting report:', error);
      setError('Failed to delete report');
    }
  };

  const handleReportGenerated = (reportData) => {
    console.log('Report generated:', reportData);
    loadReports(); // Refresh the list
    setError(null);
  };

  const getReportTypeConfig = (type) => {
    return REPORT_TYPE_CONFIG[type] || {
      name: type,
      icon: FileIcon,
      color: '#757575',
      description: 'Custom report'
    };
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';

    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM dd, yyyy h:mm a');
    } catch (_error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          AI-Generated Reports
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={refreshReports} disabled={refreshing}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowReportGeneration(true)}
            disabled={!entityId}
          >
            Generate New Report
          </Button>
        </Box>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {/* Reports Summary Cards */}
      {reports.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Object.entries(REPORT_TYPE_CONFIG).map(([type, config]) => {
            const typeReports = reports.filter(r => r.type === type);
            const IconComponent = config.icon;

            return (
              <Grid
                key={type}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <Card sx={{ border: `1px solid ${config.color}`, borderRadius: 2 }}>
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <IconComponent sx={{ color: config.color, fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ color: config.color, fontWeight: 600 }}>
                        {config.name}
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: config.color }}>
                      {typeReports.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {typeReports.length === 1 ? 'report' : 'reports'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      {/* Reports Table */}
      {reports.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No reports generated yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Generate your first AI-powered report to get insights about your organization
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowReportGeneration(true)}
              disabled={!entityId}
            >
              Generate Your First Report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Report</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Generated By</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => {
                const typeConfig = getReportTypeConfig(report.type);
                const IconComponent = typeConfig.icon;

                return (
                  <TableRow key={report.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconComponent sx={{ color: typeConfig.color }} />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {report.title || typeConfig.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {typeConfig.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={typeConfig.name}
                        size="small"
                        sx={{
                          backgroundColor: `${typeConfig.color}20`,
                          color: typeConfig.color,
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.status || 'draft'}
                        size="small"
                        color={STATUS_COLORS[report.status] || 'default'}
                        variant={report.status === 'final' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(report.createdAt || report.generatedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {report.generatedByEmail || report.createdBy || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, report)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewReport}>
          <ViewIcon sx={{ mr: 1 }} />
          View Report
        </MenuItem>
        <MenuItem onClick={handleDownloadReport}>
          <DownloadIcon sx={{ mr: 1 }} />
          Download PDF
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setDeleteDialog(true);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
      {/* View Report Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle>
          {selectedReport?.title || 'Report'}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              minHeight: 400,
              backgroundColor: '#fff'
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: reportContent }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadReport}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
      >
        <DialogTitle>Delete Report</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedReport?.title || 'this report'}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteReport}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Report Generation Dialog */}
      <ReportGeneration
        open={showReportGeneration}
        onClose={() => setShowReportGeneration(false)}
        entityId={entityId}
        onReportGenerated={handleReportGenerated}
      />
    </Box>
  );
};

export default ReportDashboard;